import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Download, Search, FileText, Calendar, Eye, Pencil, Copy, Trash2, X } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import useEscapeKey from '../../hooks/useEscapeKey';
import {
  Button, Badge, Modal, ConfirmDialog, Input, Select, Textarea,
  Table, THead, TBody, TR, TH, TD, LoadingState, EmptyState,
} from '../../components/ui';

const STATUS_OPTIONS = ['DRAFT', 'ACTIVE', 'COMPLETED'];

// Convert a UTC date string to local datetime-local input format (YYYY-MM-DDTHH:MM)
const toLocalDatetime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const offset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - offset).toISOString().slice(0, 16);
};

const defaultForm = {
  name: '',
  description: '',
  duration: 60,
  startTime: '',
  endTime: '',
  status: 'ACTIVE',
  warningLimit: 3,
  isMock: false,
  repetition: 'NONE',
  shuffleQuestions: false,
  shuffleOptions: false,
  allowRetakes: false,
  strictProctoring: false,
  passingPercentage: '',
};

const REPETITION_OPTIONS = [
  { value: 'NONE', label: 'No Repetition' },
  { value: 'DAILY', label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'YEARLY', label: 'Yearly' },
];

const ExamManagement = () => {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(defaultForm);
  const [formLoading, setFormLoading] = useState(false);
  const [editExam, setEditExam] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [dateFilterOpen, setDateFilterOpen] = useState(false);
  const [draftDateRange, setDraftDateRange] = useState({ from: '', to: '' });
  const [popoverPos, setPopoverPos] = useState(null);
  const dateFilterRef = useRef(null);
  const dateFilterBtnRef = useRef(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  const fetchExams = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (dateRange.from) params.append('startDate', dateRange.from);
      if (dateRange.to) params.append('endDate', dateRange.to);
      const res = await api.get(`/exams?${params.toString()}`);
      setExams(res.data.data?.exams || []);
    } catch (err) {
      toast.error('Failed to load exams');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchExams(); setSelectedIds(new Set()); }, [search, statusFilter, dateRange]); // eslint-disable-line

  // Close the date filter popover on an outside click.
  useEffect(() => {
    if (!dateFilterOpen) return;
    const handler = (e) => {
      if (dateFilterRef.current && !dateFilterRef.current.contains(e.target)) setDateFilterOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dateFilterOpen]);

  const openDateFilter = () => {
    // Positioned with `fixed` (anchored via the trigger button's own
    // viewport rect) rather than `absolute` inside the scrolling content
    // pane -- an `overflow-y-auto` ancestor also clips the other axis per
    // the CSS overflow spec, so an absolutely-positioned popover here gets
    // cut off at the pane's edge instead of floating freely. `fixed`
    // escapes that the same way Modal.jsx's backdrop already does.
    if (dateFilterBtnRef.current) {
      const rect = dateFilterBtnRef.current.getBoundingClientRect();
      setPopoverPos({ top: rect.bottom + 8, right: Math.max(8, window.innerWidth - rect.right) });
    }
    setDraftDateRange(dateRange);
    setDateFilterOpen((v) => !v);
  };

  const applyDateFilter = () => {
    setDateRange(draftDateRange);
    setDateFilterOpen(false);
  };

  const clearDateFilter = () => {
    setDateRange({ from: '', to: '' });
    setDraftDateRange({ from: '', to: '' });
    setDateFilterOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.duration) {
      toast.error('Name and duration are required');
      return;
    }
    if (formData.startTime && formData.endTime) {
      const start = new Date(formData.startTime);
      const end = new Date(formData.endTime);
      if (start >= end) {
        toast.error('End time must be after start time');
        return;
      }
      const diffMinutes = (end - start) / 60000;
      if (diffMinutes < formData.duration) {
        toast.error(`The time window (${Math.round(diffMinutes)} min) is shorter than the exam duration (${formData.duration} min)`);
        return;
      }
    }
    setFormLoading(true);
    try {
      const submissionData = {
        ...formData,
        startTime: formData.startTime ? new Date(formData.startTime).toISOString() : '',
        endTime: formData.endTime ? new Date(formData.endTime).toISOString() : '',
        // An empty string would otherwise cast to 0 on the backend (Mongoose
        // Number coercion), meaning "even a 0% score passes" instead of the
        // intended "no pass/fail threshold set."
        passingPercentage: formData.passingPercentage === '' || formData.passingPercentage === null
          ? null
          : Number(formData.passingPercentage),
      };

      if (editExam) {
        await api.put(`/exams/${editExam._id || editExam.id}`, submissionData);
        toast.success('Exam updated successfully');
      } else {
        await api.post('/exams', submissionData);
        toast.success('Exam created successfully');
      }
      setShowModal(false);
      setFormData(defaultForm);
      setEditExam(null);
      fetchExams();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save exam');
    } finally {
      setFormLoading(false);
    }
  };

  const handleBulkDownload = async () => {
    setBulkConfirmOpen(false);
    const downloadPromise = api.get('/results/export/bulk-all', { responseType: 'blob' });

    toast.promise(downloadPromise, {
      loading: 'Preparing bulk report (this may take a minute)...',
      success: (res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Bulk_Results_Export_${new Date().toISOString().split('T')[0]}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        return 'Download started';
      },
      error: 'Failed to download bulk results'
    });

    try {
      await downloadPromise;
    } catch {
      // toast handles it
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/exams/${deleteTarget}`);
      toast.success('Exam deleted');
      setDeleteTarget(null);
      fetchExams();
    } catch (err) {
      toast.error('Failed to delete exam');
    }
  };

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const visibleIds = exams.map((e) => e._id || e.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someVisibleSelected = visibleIds.some((id) => selectedIds.has(id));

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (allVisibleSelected) {
        const next = new Set(prev);
        visibleIds.forEach((id) => next.delete(id));
        return next;
      }
      return new Set([...prev, ...visibleIds]);
    });
  };

  const handleBulkDelete = async () => {
    try {
      const ids = Array.from(selectedIds);
      await Promise.all(ids.map((id) => api.delete(`/exams/${id}`)));
      toast.success(`${ids.length} exam(s) deleted`);
      setSelectedIds(new Set());
      fetchExams();
    } catch (err) {
      toast.error('Some exams could not be deleted');
      fetchExams();
    } finally {
      setConfirmBulkDelete(false);
    }
  };

  const handleClone = async (examId) => {
    try {
      await api.post(`/exams/${examId}/clone`);
      toast.success('Exam cloned successfully');
      fetchExams();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to clone exam');
    }
  };

  const openEdit = (exam) => {
    setEditExam(exam);
    setFormData({
      name: exam.name || exam.title || '',
      description: exam.description || '',
      duration: exam.duration,
      startTime: toLocalDatetime(exam.startTime),
      endTime: toLocalDatetime(exam.endTime),
      status: exam.status,
      warningLimit: exam.warningLimit || 3,
      isMock: exam.isMock || false,
      repetition: exam.repetition || 'NONE',
      shuffleQuestions: exam.shuffleQuestions || false,
      shuffleOptions: exam.shuffleOptions || false,
      allowRetakes: exam.allowRetakes || false,
      strictProctoring: exam.strictProctoring || false,
      passingPercentage: exam.passingPercentage ?? '',
    });
    setShowModal(true);
  };

  const openAdd = () => {
    setEditExam(null);
    setFormData(defaultForm);
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(p => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  const closeModal = useCallback(() => {
    setShowModal(false);
    setEditExam(null);
    setFormData(defaultForm);
  }, []);

  useEscapeKey(closeModal, showModal);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Exam Management</h2>
          <p className="text-slate-500 text-sm mt-1">Create and manage exams</p>
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" icon={Download} onClick={() => setBulkConfirmOpen(true)}>
            Download All Data
          </Button>
          <Button variant="primary" icon={Plus} onClick={openAdd}>
            Create Exam
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <Input
            leftIcon={Search}
            placeholder="Search exams..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="sm:w-48">
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
        </Select>
        <div className="relative" ref={dateFilterRef}>
          <Button
            ref={dateFilterBtnRef}
            variant="secondary"
            icon={Calendar}
            onClick={openDateFilter}
            className={dateRange.from || dateRange.to ? '!border-themePrimary !text-themePrimary' : ''}
          >
            {dateRange.from || dateRange.to
              ? `${dateRange.from || '…'} → ${dateRange.to || '…'}`
              : 'Date Filter'}
          </Button>
          {dateFilterOpen && popoverPos && (
            <div
              className="fixed w-72 bg-white rounded-xl shadow-lg border border-slate-200 p-4 z-50"
              style={{ top: popoverPos.top, right: popoverPos.right }}
            >
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-slate-800">Filter by schedule date</p>
                <button type="button" onClick={() => setDateFilterOpen(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">From</label>
                  <input
                    type="date"
                    value={draftDateRange.from}
                    onChange={(e) => setDraftDateRange((p) => ({ ...p, from: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-themePrimary/30 focus:border-themePrimary"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">To</label>
                  <input
                    type="date"
                    value={draftDateRange.to}
                    onChange={(e) => setDraftDateRange((p) => ({ ...p, to: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-themePrimary/30 focus:border-themePrimary"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button variant="primary" size="sm" fullWidth onClick={applyDateFilter}>Apply</Button>
                <Button variant="outline" size="sm" onClick={clearDateFilter}>Clear</Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bulk action bar -- only shown once something is selected */}
      {selectedIds.size > 0 && (
        <div className="flex items-center justify-between bg-themePrimary/5 border border-themePrimary/20 rounded-xl px-4 py-2.5">
          <span className="text-sm font-medium text-slate-700">{selectedIds.size} selected</span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>Clear</Button>
            <Button size="sm" variant="danger" onClick={() => setConfirmBulkDelete(true)}>Delete Selected</Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <LoadingState label="Loading exams..." />
        ) : exams.length === 0 ? (
          <EmptyState icon={FileText} title="No exams found" description="Create your first exam to get started." />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH className="w-10">
                  <input
                    type="checkbox"
                    className="rounded border-slate-300 text-themePrimary focus:ring-themePrimary/40 cursor-pointer"
                    checked={allVisibleSelected}
                    ref={(el) => { if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected; }}
                    onChange={toggleSelectAll}
                    aria-label="Select all exams on this page"
                  />
                </TH>
                <TH>Exam Title</TH>
                <TH>Created By</TH>
                <TH>Duration & Warning</TH>
                <TH>Schedule / Start Time</TH>
                <TH>Status</TH>
                <TH>Actions</TH>
              </TR>
            </THead>
            <TBody>
              {exams.map((exam) => {
                const id = exam._id || exam.id;
                const warningLimit = exam.warningLimit ?? 3;
                return (
                  <TR key={id}>
                    <TD>
                      <input
                        type="checkbox"
                        className="rounded border-slate-300 text-themePrimary focus:ring-themePrimary/40 cursor-pointer"
                        checked={selectedIds.has(id)}
                        onChange={() => toggleSelected(id)}
                        aria-label={`Select ${exam.name}`}
                      />
                    </TD>
                    <TD className="max-w-[190px]">
                      <p className="font-medium text-slate-900 truncate">{exam.name}</p>
                      {exam.description && (
                        <p className="text-xs text-slate-500 mt-0.5 truncate">{exam.description}</p>
                      )}
                    </TD>
                    <TD>
                      {exam.createdBy ? (
                        <div>
                          <p className="text-slate-800">{exam.createdBy.name}</p>
                          <p className="text-xs text-slate-400">{exam.createdBy.email}</p>
                        </div>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </TD>
                    <TD>
                      <Badge variant="neutral">
                        {exam.duration}m · {warningLimit > 0 ? `${warningLimit} warning${warningLimit !== 1 ? 's' : ''}` : 'No warnings'}
                      </Badge>
                    </TD>
                    <TD className="whitespace-nowrap">
                      {exam.startTime
                        ? new Date(exam.startTime).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
                        : '—'}
                    </TD>
                    <TD><Badge>{exam.status}</Badge></TD>
                    <TD>
                      <div className="flex items-center gap-0.5">
                        <Link to={`/admin/exams/${id}`} title="View details">
                          <Button variant="ghost" size="sm" icon={Eye} className="!px-1.5" />
                        </Link>
                        <Button variant="ghost" size="sm" icon={Pencil} className="!px-1.5" title="Edit" onClick={() => openEdit(exam)} />
                        <Button variant="ghost" size="sm" icon={Copy} className="!px-1.5" title="Clone" onClick={() => handleClone(id)} />
                        <Button
                          variant="ghost" size="sm" icon={Trash2} className="!px-1.5 !text-red-600 hover:!bg-red-50" title="Delete"
                          onClick={() => setDeleteTarget(id)}
                        />
                      </div>
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal
        open={showModal}
        onClose={closeModal}
        title={editExam ? 'Edit Exam' : 'Create New Exam'}
        size="lg"
        footer={
          <>
            <Button variant="outline" onClick={closeModal}>Cancel</Button>
            <Button variant="primary" loading={formLoading} type="submit" form="exam-form">
              {editExam ? 'Update Exam' : 'Create Exam'}
            </Button>
          </>
        }
      >
        <form id="exam-form" onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Input
              label="Exam Name *"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g. Mid-Term Mathematics Exam"
              required
            />
          </div>
          <div className="sm:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Input
              label="Duration (minutes) *"
              name="duration"
              type="number"
              value={formData.duration}
              onChange={handleChange}
              min={1}
              required
            />
            <Input
              label="Warning Limit"
              name="warningLimit"
              type="number"
              value={formData.warningLimit}
              onChange={handleChange}
              min={1}
            />
            <Input
              label="Passing percentage"
              name="passingPercentage"
              type="number"
              min="0"
              max="100"
              placeholder="e.g. 50"
              value={formData.passingPercentage ?? ''}
              onChange={handleChange}
            />
            <Select label="Status" name="status" value={formData.status} onChange={handleChange}>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </Select>
          </div>
          <Input
            label="Start Time"
            name="startTime"
            type="datetime-local"
            value={formData.startTime}
            onChange={handleChange}
          />
          <Input
            label="End Time"
            name="endTime"
            type="datetime-local"
            value={formData.endTime}
            onChange={handleChange}
          />
          <div className="flex items-center gap-3 bg-themePrimary/5 border border-themePrimary/20 rounded-lg px-4 py-3">
            <input
              id="isMock"
              name="isMock"
              type="checkbox"
              checked={formData.isMock}
              onChange={handleChange}
              className="w-4 h-4 accent-themePrimary flex-shrink-0"
            />
            <div>
              <label htmlFor="isMock" className="text-sm font-medium text-themePrimary cursor-pointer">
                Mock / Practice Test
              </label>
              <p className="text-xs text-themePrimary/80 mt-0.5">
                Students can attempt this unlimited times. Results shown immediately. No proctoring required.
              </p>
            </div>
          </div>
          <Select label="Repetition" name="repetition" value={formData.repetition} onChange={handleChange}>
            {REPETITION_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </Select>
          <div className="border border-slate-200 rounded-lg px-4 py-3 space-y-2.5">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Exam Behavior</p>
            {[
              { name: 'shuffleQuestions', label: 'Shuffle question order', hint: 'Each student sees questions in a different order.' },
              { name: 'allowRetakes', label: 'Allow retakes', hint: 'Students can retake this exam as many times as needed; each retake replaces the previous attempt and score.' },
            ].map(({ name, label, hint }) => (
              <div key={name} className="flex items-start gap-3">
                <input
                  id={name}
                  name={name}
                  type="checkbox"
                  checked={!!formData[name]}
                  onChange={handleChange}
                  className="w-4 h-4 mt-0.5 accent-themePrimary flex-shrink-0"
                />
                <div>
                  <label htmlFor={name} className="text-sm font-medium text-slate-700 cursor-pointer">{label}</label>
                  <p className="text-xs text-slate-500 mt-0.5">{hint}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="border border-slate-200 rounded-lg px-4 py-3 space-y-2.5">
            {[
              { name: 'shuffleOptions', label: 'Shuffle answer options', hint: 'Each student sees MCQ/multi-select options in a different order.' },
              // Commented out for now -- not implemented yet (see Exam.strictProctoring).
              // { name: 'strictProctoring', label: 'Strict proctoring', hint: "Reserved for stricter future enforcement (screen-recording, enhanced lockdown). Not implemented yet, so this doesn't change proctoring on this exam." },
            ].map(({ name, label, hint }) => (
              <div key={name} className="flex items-start gap-3">
                <input
                  id={name}
                  name={name}
                  type="checkbox"
                  checked={!!formData[name]}
                  onChange={handleChange}
                  className="w-4 h-4 mt-0.5 accent-themePrimary flex-shrink-0"
                />
                <div>
                  <label htmlFor={name} className="text-sm font-medium text-slate-700 cursor-pointer">{label}</label>
                  <p className="text-xs text-slate-500 mt-0.5">{hint}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="sm:col-span-2">
            <Textarea
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              placeholder="Enter exam description or instructions..."
            />
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        variant="danger"
        title="Delete this exam?"
        description="This will deactivate the exam. This action cannot be undone from this screen."
        confirmLabel="Delete"
      />

      <ConfirmDialog
        open={bulkConfirmOpen}
        onClose={() => setBulkConfirmOpen(false)}
        onConfirm={handleBulkDownload}
        title="Download all results?"
        description="Downloading all student results for every exam may take a few moments and consume significant memory. Do you want to proceed?"
        confirmLabel="Download"
      />

      <ConfirmDialog
        open={confirmBulkDelete}
        onClose={() => setConfirmBulkDelete(false)}
        onConfirm={handleBulkDelete}
        variant="danger"
        title={`Delete ${selectedIds.size} selected exam(s)?`}
        description="This will deactivate each selected exam. This action cannot be undone from this screen."
        confirmLabel="Delete"
      />
    </div>
  );
};

export default ExamManagement;
